import { alpha, createTheme } from '@mui/material';

import { ColorTokens } from '../types';

import { PERFORMANCE_STATUS } from '@/features/starvationBidder/components/diagnostics/performanceMonitoring/constants';

const primaryColor = {
  50: '#F2F6FE',
  100: '#D3E0FB',
  200: '#A8C1F7',
  300: '#7CA1F3',
  400: '#5182EF',
  500: '#2563EB', // main
  600: '#1452D9',
  700: '#1145B9',
  800: '#0E3998',
  900: '#0B2D78',
  1000: '#082158',
  customLight: '#FAFCFF',
  customMedium: '#DFE8FC',
};

const secondaryColor = {
  50: '#FAF2FE',
  100: '#EFD4FB',
  200: '#DEA8F7',
  300: '#CE7DF3',
  400: '#BD51EF',
  500: '#AD26EB', // main
  600: '#9B14DA',
  700: '#8411B9',
  800: '#6D0E99',
  900: '#560B78',
  1000: '#560B78',
};

export const grey = {
  25: '#FCFCFD', // lightest
  50: '#F9FAFB', // light
  100: '#F2F4F7',
  200: '#E4E7EC', // divider
  300: '#D0D5DD',
  400: '#98A2B3',
  500: '#667085', // medium
  600: '#475467',
  700: '#344054', // main
  800: '#1D2939',
  900: '#101828', // dark
};

export const colorTokens: ColorTokens = {
  green: {
    10: '#EBFFEE',
    100: '#14AE5C',
    200: '#02542D',
  },
  orange: {
    50: '#FFF6ED',
    700: '#C4320A',
  },
  yellow: {
    5: '#FFFCF5',
    10: '#FFFBEB',
    100: '#E5A000',
    200: '#BF6A02',
  },
  red: {
    5: '#FFFBFA',
    10: '#FEE9E7',
    100: '#EC221F',
    200: '#C00F0C',
  },
  steel: {
    50: '#F0F9FF',
    700: '#026AA2',
  },

  // mapbox plot colors
  mapboxBucketColors: {
    map1: ['#F0DBFC', '#E0B8F8', '#CC8DF4', '#B75DEF'],
    map2: ['#B3E599', '#8DD15C', '#66B338', '#4C8F28'],
    map3: ['#FFB8E0', '#FF8AC9', '#FF5AAD', '#E62890'],
  },

  // performance monitoring status colors
  performanceStatusColors: {
    [PERFORMANCE_STATUS.EFFICIENT_ON_TRACK]: primaryColor[300],
    [PERFORMANCE_STATUS.EFFICIENT_AT_RISK]: secondaryColor[300],
    [PERFORMANCE_STATUS.INEFFICIENT_ON_TRACK]: '#fed093',
    [PERFORMANCE_STATUS.INEFFICIENT_AT_RISK]: '#fcafa8',
  },

  // bdi/cdi priority group plot colors
  bdiCdiPlotColors: {
    L: { stroke: '#3bceac', fill: '#a2f1e1' },
    M: { stroke: '#ffd23f', fill: '#ffed99' },
    H: { stroke: '#ee4266', fill: '#ff9aa2' },
    VH: { stroke: '#540d6e', fill: '#d291e4' },
  },
};

const plusJakartaSansFontFamily = [
  '"Plus Jakarta Sans"',
  '-apple-system', // macOS / iOS
  'BlinkMacSystemFont', // Chrome Mac
  '"Segoe UI"', // Windows
  'Roboto', // Android / fallback
  'Helvetica',
  'Arial',
  'sans-serif',
].join(',');

export const interFontFamily = [
  '"Inter"',
  '-apple-system', // macOS / iOS
  'BlinkMacSystemFont', // Chrome Mac
  '"Segoe UI"', // Windows
  'Roboto', // Android / fallback
  'Helvetica',
  'Arial',
  'sans-serif',
].join(',');

const theme = createTheme({
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1272,
      xl: 1536,
    },
  },
  customShadows: {
    xs: `0px 1px 2px 0px ${alpha(grey[900], 0.05)}`,
    sm: `0px 1px 2px 0px ${alpha(grey[900], 0.06)}, 0px 1px 3px 0px ${alpha(grey[900], 0.1)}`,
    md: `0px 2px 4px -2px ${alpha(grey[900], 0.06)}, 0px 4px 8px -2px ${alpha(grey[900], 0.1)}`,
    lg: `0px 4px 6px -2px ${alpha(grey[900], 0.05)}, 0px 12px 16px -4px ${alpha(grey[900], 0.1)}`,
    xl: `0px 8px 8px -4px ${alpha(grey[900], 0.04)}, 0px 20px 24px -4px ${alpha(grey[900], 0.1)}`,
    200: `0px 1px 4px 0px ${alpha(grey[900], 0.05)}, 0px 1px 4px 0px ${alpha(grey[900], 0.1)}`,
    400: `0px 4px 4px -4px ${alpha(grey[900], 0.05)}, 0px 16px 32px -4px ${alpha(grey[900], 0.1)}`,
  },
  palette: {
    primary: {
      main: primaryColor[500],
      50: primaryColor[50],
      100: primaryColor[100],
      200: primaryColor[200],
      300: primaryColor[300],
      400: primaryColor[400],
      500: primaryColor[500], // main
      600: primaryColor[600],
      700: primaryColor[700], // dark
      800: primaryColor[800],
      900: primaryColor[900],
      1000: primaryColor[1000],
      customLight: primaryColor.customLight,
      customMedium: primaryColor.customMedium,
    },
    secondary: {
      main: secondaryColor[500],
      50: secondaryColor[50],
      100: secondaryColor[100],
      200: secondaryColor[200],
      300: secondaryColor[300],
      400: secondaryColor[400],
      500: secondaryColor[500], // main
      600: secondaryColor[600],
      700: secondaryColor[700],
      800: secondaryColor[800],
      900: secondaryColor[900],
      1000: secondaryColor[1000],
    },
    text: {
      primary: grey[700], // main
      secondary: grey[500], // medium
      tertiary: grey[900], // dark
    },
    grey: {
      25: grey[25], // lightest
      50: grey[50], // light
      100: grey[100],
      200: grey[200],
      300: grey[300],
      400: grey[400],
      500: grey[500], // medium
      600: grey[600],
      700: grey[700], // main
      800: grey[800],
      900: grey[900], // dark
    },
    divider: grey[200],
  },

  typography: {
    fontFamily: plusJakartaSansFontFamily,
    h3: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: '2.625rem',
      color: grey[900],
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: '2.625rem',
      color: grey[900],
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: '1.875rem',
      color: grey[900],
    },
    body1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: '1.375rem',
    },
    body2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: '1.3125rem',
    },
    body3: {
      fontWeight: 500,
      fontSize: '0.75rem',
      lineHeight: '1.125rem',
    },
    body14: {
      fontFamily: interFontFamily,
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    body16: {
      fontFamily: interFontFamily,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: '1.3125rem',
      textTransform: 'none',
    },
    subtitleNumbers: {
      fontFamily: interFontFamily,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    subtitleLabel: {
      fontFamily: interFontFamily,
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: '1.25rem',
    },
  },

  components: {
    MuiAutocomplete: {
      defaultProps: {
        loadingText: 'Loading..',
        slotProps: {
          listbox: {
            sx: {
              py: 0,
            },
          },
          paper: {
            elevation: 0,
            sx: {
              fontWeight: 400,
              color: 'text.tertiary',
              boxShadow: (theme) => theme.customShadows.xl,
            },
          },
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
          },
        },
      },
    },

    MuiMenu: {
      defaultProps: {
        slotProps: {
          list: {
            sx: {
              py: 0,
            },
          },
        },
      },
    },

    MuiPopover: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 0,
            sx: {
              boxShadow: (theme) => theme.customShadows.xl,
              mt: 0.5,
            },
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        sx: {
          borderRadius: 1,
        },
      },
    },
  },
});

export default theme;
